'use client'
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  loginRequest,
  sendOTPRequest,
  signOut,
  signupRequest,
} from '@/services/auth/auth.service'
import { clearAuthTokens, isLoggedIn, setAuthTokens } from 'axios-jwt'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import {
  bootstrapAccessToken,
  getBootstrapSessionExpired,
  getBootstrapSessionTransient,
} from '@/services/api'
import { errorToast, infoToast } from '@/lib/toast'
import { useAuthMe } from '@/hooks/useAuthMe.hook'
import { SignupRequestDTO } from '@/services/auth/auth.dto'
import { User } from '@/types/models/User'
import { CreateAccountDTO } from '@/services/accounts/account.dto'
import { Account } from '@/types/models/Account'
import { useAccount } from '@/services/accounts/account.hook'
import { Routes } from '@/config/routes.config'
import { mutate } from 'swr'

export interface ContextAuth {
  authenticated: boolean
  sendOTP: (email: string) => Promise<void>
  signup: (data: SignupRequestDTO) => Promise<void>
  login: (code: string) => Promise<void>
  logout: () => Promise<void>
  email: string | undefined
  setEmail: (d: string) => void
  user?: User
  account?: Account
  onNewAccount: (data: CreateAccountDTO) => void
  refreshUser: () => void
  refreshAccount: () => void
}

const AuthContext = createContext<ContextAuth | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

const AuthProvider = ({ children }: AuthProviderProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const [authenticated, setAuthenticated] = useState<boolean>(false)
  const [email, setEmail] = useState<string | undefined>()

  const { user, refresh: refreshUser } = useAuthMe({ authenticated })
  const { data: account, refresh: refreshAccount } = useAccount({
    accountId: user?.accountId,
  })

  const sendOTP = useCallback(
    async (inputEmail: string) => {
      try {
        await sendOTPRequest({ email: inputEmail })
        setEmail(inputEmail)
        infoToast('Un code de connexion a été envoyé à ton adresse email')
        router.push(`${Routes.LoginOTP.href}?email=${inputEmail}`)
      } catch (e: any) {
        errorToast(`Une erreur est survenue: ${e.message}`)
      }
    },
    [router]
  )

  const onNewAccount = useCallback(
    (data: CreateAccountDTO) => {
      infoToast('Vérifiez votre email pour activer votre compte')
      setEmail(data.member!.email)
      router.push(
        `/auth/signup/verify${
          data.member!.email
            ? `?email=${encodeURIComponent(data.member!.email)}`
            : ''
        }`
      )
    },
    [router]
  )

  const signup = useCallback(
    async (data: SignupRequestDTO) => {
      try {
        await signupRequest(data)
        setEmail(data.email)
        infoToast('Vérifie ton email pour activer ton compte')
        router.push(`${Routes.LoginOTP.href}?email=${data.email}`)
      } catch (e: any) {
        errorToast(`Une erreur est survenue: ${e.message}`)
      }
    },
    [router]
  )

  const checkAuth = useCallback(async () => {
    const hasRefreshToken = await isLoggedIn()
    setAuthenticated(hasRefreshToken)
    if (
      !hasRefreshToken &&
      !pathname.includes('cast') &&
      !pathname.includes('auth')
    ) {
      // A TRANSIENT bootstrap failure (network / 5xx) leaves the session valid
      // server-side — do NOT log the user out. Stay put; SWR / the next reload
      // recovers (bootstrap is retried on the next request). See services/api.ts.
      if (getBootstrapSessionTransient()) {
        return
      }
      // Genuine expiry → carry ?reason=expired so the login page renders the calm
      // "session expired" banner (SessionExpiredBanner) instead of a bare form.
      router.push(
        getBootstrapSessionExpired()
          ? '/auth/login?reason=expired'
          : '/auth/login'
      )
      return
    }
  }, [pathname, router])

  const login = useCallback(
    async (code: string) => {
      try {
        const response = await loginRequest({
          email: email!,
          otp: code,
        })
        // Refresh token is now an HttpOnly cookie set by the API; only the
        // access token comes back in the JSON body. axios-jwt requires a
        // non-empty refreshToken in storage; we never read it back.
        setAuthTokens({
          accessToken: response.accessToken,
          refreshToken: 'cookie',
        })
        checkAuth()
        refreshUser()
        const redirectUrl = response.isFirstLogin ? '/onboarding' : '/'
        router.push(redirectUrl)
        infoToast('Bienvenue!')
      } catch (e) {
        console.error(e)
      }
    },
    [checkAuth, email, refreshUser, router]
  )

  useEffect(() => {
    const searchEmail = searchParams.get('email')
    if (!email && searchEmail) {
      setEmail(searchEmail)
    }
  }, [email, searchParams])

  const oauthRef = useRef(false)

  useEffect(() => {
    if (pathname.includes('oauth') && !oauthRef.current) {
      oauthRef.current = true
      const accessToken = searchParams.get('access_token')
      const onboardingRequired = searchParams.get('onboarding') === 'true'
      // Refresh token is delivered as an HttpOnly cookie in the same redirect
      // response; only the access token rides in the URL.
      if (accessToken) {
        setAuthTokens({
          accessToken: accessToken,
          refreshToken: 'cookie',
        })
        checkAuth()

        const redirectUrl = onboardingRequired ? '/onboarding' : '/'
        router.push(redirectUrl)
        infoToast('Bienvenue!')
      } else {
        errorToast("Probleme d'authentification")
        router.push('/auth/login')
      }
    }
  }, [checkAuth, pathname, router, searchParams])

  const logout = useCallback(async () => {
    try {
      await signOut()

      oauthRef.current = false
      setAuthenticated(false)
      await mutate(() => true, undefined, { revalidate: false })
      router.push('/auth/login')
    } catch (e) {
      errorToast(`Probleme de deconnexion...`)
    }
    clearAuthTokens()
    checkAuth()
  }, [checkAuth, router])

  // Bootstrap (mount-only): attempt one POST /auth/refresh. The browser
  // auto-attaches the HttpOnly refresh cookie if present; on success the
  // access token is populated in module-scope storage and isLoggedIn()
  // returns true. On failure (no cookie / expired refresh) we fall through
  // to checkAuth's redirect-to-login path. Skip bootstrap for public paths
  // (cast / auth).
  //
  // Polling (always-on, ref-based): the 5s interval calls the LATEST
  // checkAuth via a ref so it stays in sync with the current pathname.
  // Without this, the interval captures the pathname at first mount and
  // wrongly redirects after the user navigates between routes. The
  // bootstrap → polling chain is gated by a `cancelled` flag so an aborted
  // navigation can't strand an orphan interval.
  const checkAuthRef = useRef(checkAuth)
  useEffect(() => {
    checkAuthRef.current = checkAuth
  }, [checkAuth])

  useEffect(() => {
    let cancelled = false
    let intervalId: ReturnType<typeof setInterval> | null = null
    const tick = () => checkAuthRef.current()
    const startPolling = () => {
      if (cancelled) return
      tick()
      intervalId = setInterval(tick, 5000)
    }

    // Snapshot the path at mount; the interval reads the current pathname
    // via the ref each tick (checkAuth closes over pathname), so this only
    // matters for the bootstrap branch decision.
    const mountPath =
      typeof window !== 'undefined' ? window.location.pathname : ''
    if (mountPath.includes('cast') || mountPath.includes('auth')) {
      startPolling()
    } else {
      bootstrapAccessToken().finally(startPolling)
    }

    return () => {
      cancelled = true
      if (intervalId) clearInterval(intervalId)
    }
    // Mount-only by design: bootstrap fires once, polling stays alive for
    // the lifetime of the AuthProvider. checkAuth changes are picked up
    // through the ref, not via re-running this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const context = useMemo(
    () => ({
      authenticated,
      sendOTP,
      login,
      signup,
      logout,
      email,
      setEmail,
      user,
      onNewAccount,
      account,
      refreshUser,
      refreshAccount,
    }),
    [
      authenticated,
      sendOTP,
      login,
      signup,
      logout,
      email,
      user,
      onNewAccount,
      account,
      refreshUser,
      refreshAccount,
    ]
  )
  return <AuthContext.Provider value={context}>{children}</AuthContext.Provider>
}

function useAuth(): ContextAuth {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider')
  }
  return context
}

export { AuthProvider, useAuth }
